/**
 * DynamicTableHeader — sortable column headers for DynamicTableRenderer.
 * Only columns flagged as sortable show sort affordances.
 */
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { DynamicField, DynamicTableSortState } from '../../types/dynamicEntity';

interface DynamicTableHeaderProps {
  columns: DynamicField[];
  sort?: DynamicTableSortState | null;
  onSortChange?: (sort: DynamicTableSortState | null) => void;
  showStatusColumn?: boolean;
  showActionColumn?: boolean;
}

export function DynamicTableHeader({
  columns,
  sort,
  onSortChange,
  showStatusColumn = true,
  showActionColumn = false,
}: DynamicTableHeaderProps) {
  const handleSort = (field: DynamicField) => {
    if (!onSortChange || !field.displayConfig?.sortable) return;
    if (sort?.field === field.internalName) {
      if (sort.direction === 'asc') {
        onSortChange({ field: field.internalName, direction: 'desc' });
      } else {
        onSortChange(null); // clear sort
      }
    } else {
      onSortChange({ field: field.internalName, direction: 'asc' });
    }
  };

  return (
    <tr className="bg-muted/40 border-b border-border">
      {columns.map((col) => {
        const isSortable = col.displayConfig?.sortable && !!onSortChange;
        const isActive = sort?.field === col.internalName;

        return (
          <th
            key={col.fieldId}
            onClick={isSortable ? () => handleSort(col) : undefined}
            className={`px-3 py-2 text-[12px] font-semibold text-foreground whitespace-nowrap select-none ${
              isSortable ? 'cursor-pointer hover:bg-muted/60 transition-colors' : ''
            }`}
          >
            <div className="flex items-center gap-1">
              <span>{col.label}</span>
              {isSortable && (
                <span className={`transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground/40'}`}>
                  {isActive && sort?.direction === 'asc' ? (
                    <ArrowUp className="w-3 h-3" />
                  ) : isActive && sort?.direction === 'desc' ? (
                    <ArrowDown className="w-3 h-3" />
                  ) : (
                    <ArrowUpDown className="w-3 h-3" />
                  )}
                </span>
              )}
            </div>
          </th>
        );
      })}
      {showStatusColumn && (
        <th className="px-3 py-2 text-[12px] font-semibold text-foreground whitespace-nowrap">
          Status
        </th>
      )}
      {showActionColumn && (
        <th className="px-3 py-2 text-[12px] font-semibold text-foreground w-10" />
      )}
    </tr>
  );
}
